<template>
  <div class="page">

    <!-- 顶部栏 -->
    <header class="topbar">
      <div class="tb-back" @click="$router.back()">
        <i class="fa-solid fa-chevron-left"></i>
      </div>
      <h1 class="tb-title">补贴专区</h1>
      <FavGuide v-model:visible="showFavGuide" theme="light" />
    </header>

    <!-- 顶部Banner -->
    <div class="banner">
      <div class="banner-text">
        <h2>补贴专区</h2>
        <p>政府补贴 · 品质好物 · 超值优惠</p>
      </div>
      <div class="banner-icons">
        <i class="fa-solid fa-tag b-icon-1"></i>
        <i class="fa-solid fa-store b-icon-2"></i>
        <i class="fa-solid fa-percent b-icon-3"></i>
      </div>
    </div>

    <!-- 补贴政策 -->
    <div class="section" v-if="policies.length > 0">
      <div class="section-header">
        <h3 class="section-title"><i class="fa-solid fa-file-lines"></i> 补贴政策</h3>
      </div>
      <div class="policy-scroll">
        <div v-for="p in policies" :key="p.id" class="policy-card" @click="showPolicyDetail(p)">
          <div class="pc-icon">{{ getPolicyIcon(p.type) }}</div>
          <div class="pc-info">
            <h4 class="pc-title">{{ p.title }}</h4>
            <p class="pc-type">{{ p.type }}</p>
          </div>
          <div class="pc-subsidy">
            <span class="pc-subsidy-label">最高补贴</span>
            <span class="pc-subsidy-num">¥{{ p.max_subsidy }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 参与补贴的商家 -->
    <div class="section" v-if="merchants.length > 0">
      <div class="section-header">
        <h3 class="section-title"><i class="fa-solid fa-store"></i> 参与补贴的商家</h3>
        <span class="section-count">共{{ merchants.length }}家</span>
      </div>

      <div class="merchant-list">
        <div v-for="m in merchants" :key="m.id" class="merchant-card">
          <!-- 商家名片 -->
          <div class="mc-header" @click="goShop(m.id)">
            <img :src="m.main_image || '/default-shop.png'" class="mc-avatar" />
            <div class="mc-info">
              <h4 class="mc-name">{{ m.name }}</h4>
              <div class="mc-meta">
                <span class="mc-rating"><i class="fa-solid fa-star"></i> {{ m.rating }}</span>
                <span class="mc-category" v-if="m.category">{{ m.category.name }}</span>
              </div>
              <p class="mc-address" v-if="m.address">
                <i class="fa-solid fa-location-dot"></i> {{ m.address }}
              </p>
              <p class="mc-phone" v-if="m.phone">
                <i class="fa-solid fa-phone"></i> {{ m.phone }}
              </p>
            </div>
            <i class="fa-solid fa-chevron-right mc-arrow"></i>
          </div>

          <!-- 补贴商品 -->
          <div class="mc-products" v-if="m.subsidy_products && m.subsidy_products.length > 0">
            <div class="mc-products-title">
              <i class="fa-solid fa-fire"></i> 补贴商品
            </div>
            <div class="mc-products-scroll">
              <div v-for="p in m.subsidy_products" :key="p.id" class="product-item" @click="goProduct(p.id)">
                <img :src="p.image || '/default-product.png'" class="pi-img" />
                <div class="pi-info">
                  <h5 class="pi-name">{{ p.name }}</h5>
                  <div class="pi-prices">
                    <span class="pi-original">¥{{ p.original_price }}</span>
                    <span class="pi-subsidy">¥{{ p.subsidy_price }}</span>
                  </div>
                  <span class="pi-tag">补贴</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 优惠信息 -->
          <div class="mc-coupons" v-if="m.coupons && m.coupons.length > 0">
            <div class="mc-coupons-title">
              <i class="fa-solid fa-ticket"></i> 优惠
            </div>
            <div class="coupon-list">
              <div v-for="c in m.coupons" :key="c.id" class="coupon-item">
                <div class="ci-left">
                  <span class="ci-reduce">¥{{ c.reduce_amount }}</span>
                  <span class="ci-threshold" v-if="c.threshold > 0">满{{ c.threshold }}可用</span>
                  <span class="ci-threshold" v-else>无门槛</span>
                </div>
                <div class="ci-right">
                  <span class="ci-name">{{ c.name }}</span>
                  <span class="ci-valid">{{ c.valid_end }}截止</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="!loading && policies.length === 0 && merchants.length === 0" class="empty">
      <i class="fa-solid fa-box-open"></i>
      <p>暂无补贴信息</p>
    </div>

    <!-- 加载中 -->
    <div v-if="loading" class="loading">
      <i class="fa-solid fa-spinner fa-spin"></i>
      <span>加载中...</span>
    </div>

    <!-- 政策详情弹窗 -->
    <div v-if="currentPolicy" class="modal-mask" @click.self="currentPolicy = null">
      <div class="modal-box">
        <div class="modal-header">
          <h3>{{ currentPolicy.title }}</h3>
          <div class="modal-close" @click="currentPolicy = null">
            <i class="fa-solid fa-xmark"></i>
          </div>
        </div>
        <div class="modal-body">
          <div class="modal-section">
            <h4>📋 政策简介</h4>
            <p>{{ currentPolicy.content || '该政策旨在推动消费升级，促进绿色消费，政府提供专项补贴资金，符合条件的市民可在线申请。' }}</p>
          </div>
          <div class="modal-section">
            <h4>💰 补贴标准</h4>
            <div class="modal-highlight">
              <span>最高补贴</span>
              <strong>¥{{ currentPolicy.max_subsidy }}</strong>
            </div>
          </div>
          <div class="modal-section" v-if="currentPolicy.steps && currentPolicy.steps.length > 0">
            <h4>🔄 申请流程</h4>
            <div class="flow-steps">
              <div v-for="(step, index) in currentPolicy.steps" :key="index" class="flow-step">
                <span class="fs-num">{{ index + 1 }}</span>
                <span>{{ step }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn" @click="currentPolicy = null">我知道了</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import FavGuide from '@/components/FavGuide.vue'

export default {
  name: 'PolicyList',
  components: { FavGuide },

  data() {
    return {
      policies: [],
      merchants: [],
      loading: false,
      currentPolicy: null,
      showFavGuide: false
    }
  },

  mounted() {
    this.fetchSubsidyZone()
  },

  methods: {
    async fetchSubsidyZone() {
      this.loading = true
      try {
        const r = await this.$api.get('/api/v1/subsidy/zone')
        const data = r.data.data || {}
        this.policies = data.policies || []
        this.merchants = data.merchants || []
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    getPolicyIcon(type) {
      const icons = {
        '以旧换新': '🔄',
        '政府补贴': '🏛️'
      }
      return icons[type] || '📋'
    },

    showPolicyDetail(policy) {
      this.currentPolicy = policy
    },

    goShop(id) {
      this.$router.push(`/shop/${id}`)
    },

    goProduct(id) {
      this.$router.push(`/product/${id}`)
    }
  }
}
</script>

<style scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

.page {
  width: 100%;
  min-height: 100vh;
  background: #f4f8fb;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: #1f2937;
  padding-top: 48px;
  padding-bottom: 20px;
}

/* ================================================================
   顶部栏
   ================================================================ */
.topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 12px;
  background: #fff;
  border-bottom: 1px solid #f3f4f6;
}

.tb-back {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  color: #1f2937;
}

.tb-back:active { background: #f3f4f6; }
.tb-back i { font-size: 14px; }

.tb-title {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
}

.tb-spacer { width: 32px; }

/* ================================================================
   Banner
   ================================================================ */
.banner {
  margin: 12px;
  padding: 20px;
  border-radius: 16px;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #dc2626 100%);
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
  position: relative;
}

.banner::after {
  content: '';
  position: absolute;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(255,255,255,.08);
  top: -40px;
  right: -20px;
}

.banner-text h2 {
  font-size: 22px;
  font-weight: 900;
  letter-spacing: 2px;
}

.banner-text p {
  font-size: 12px;
  opacity: .8;
  margin-top: 4px;
}

.banner-icons {
  display: flex;
  gap: 8px;
  position: relative;
  z-index: 1;
}

.b-icon-1 { font-size: 24px; opacity: .5; }
.b-icon-2 { font-size: 30px; opacity: .7; }
.b-icon-3 { font-size: 24px; opacity: .5; }

/* ================================================================
   Section 通用
   ================================================================ */
.section {
  margin: 0 12px 16px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-title i {
  color: #f97316;
  font-size: 14px;
}

.section-count {
  font-size: 12px;
  color: #9ca3af;
}

/* ================================================================
   政策卡片（横向滚动）
   ================================================================ */
.policy-scroll {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: touch;
}

.policy-scroll::-webkit-scrollbar { display: none; }

.policy-card {
  min-width: 200px;
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: transform .2s;
  flex-shrink: 0;
}

.policy-card:active { transform: scale(.97); }

.pc-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff7ed;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.pc-info { flex: 1; }

.pc-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pc-type {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.pc-subsidy {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 4px;
}

.pc-subsidy-label {
  font-size: 10px;
  color: #9ca3af;
}

.pc-subsidy-num {
  font-size: 20px;
  font-weight: 900;
  color: #f97316;
}

/* ================================================================
   商家卡片
   ================================================================ */
.merchant-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.merchant-card {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
}

/* 商家名片 */
.mc-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  cursor: pointer;
  transition: background .2s;
}

.mc-header:active { background: #f9fafb; }

.mc-avatar {
  width: 56px;
  height: 56px;
  min-width: 56px;
  border-radius: 12px;
  object-fit: cover;
  background: #f3f4f6;
}

.mc-info {
  flex: 1;
  min-width: 0;
}

.mc-name {
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mc-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.mc-rating {
  font-size: 12px;
  color: #f97316;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 2px;
}

.mc-rating i { font-size: 10px; }

.mc-category {
  font-size: 11px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 1px 6px;
  border-radius: 4px;
}

.mc-address, .mc-phone {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.mc-address i, .mc-phone i { font-size: 10px; }

.mc-arrow {
  color: #d1d5db;
  font-size: 12px;
}

/* 补贴商品 */
.mc-products {
  padding: 0 14px 12px;
  border-top: 1px solid #f3f4f6;
}

.mc-products-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  padding: 10px 0 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.mc-products-title i {
  color: #ef4444;
  font-size: 12px;
}

.mc-products-scroll {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.mc-products-scroll::-webkit-scrollbar { display: none; }

.product-item {
  min-width: 130px;
  max-width: 130px;
  background: #fafafa;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: transform .2s;
  flex-shrink: 0;
}

.product-item:active { transform: scale(.97); }

.pi-img {
  width: 100%;
  height: 100px;
  object-fit: cover;
  background: #f3f4f6;
}

.pi-info {
  padding: 8px;
}

.pi-name {
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pi-prices {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 4px;
}

.pi-original {
  font-size: 11px;
  color: #9ca3af;
  text-decoration: line-through;
}

.pi-subsidy {
  font-size: 15px;
  font-weight: 900;
  color: #f97316;
}

.pi-tag {
  display: inline-block;
  font-size: 10px;
  color: #fff;
  background: linear-gradient(135deg, #f97316, #ea580c);
  padding: 1px 6px;
  border-radius: 4px;
  margin-top: 4px;
}

/* 优惠券 */
.mc-coupons {
  padding: 0 14px 14px;
  border-top: 1px solid #f3f4f6;
}

.mc-coupons-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  padding: 10px 0 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.mc-coupons-title i {
  color: #f97316;
  font-size: 12px;
}

.coupon-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.coupon-item {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #fff7ed, #fef3c7);
  border: 1px dashed #fbbf24;
  border-radius: 8px;
  overflow: hidden;
}

.ci-left {
  min-width: 80px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-right: 1px dashed #fbbf24;
}

.ci-reduce {
  font-size: 18px;
  font-weight: 900;
  color: #ea580c;
}

.ci-threshold {
  font-size: 10px;
  color: #9a3412;
}

.ci-right {
  flex: 1;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ci-name {
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ci-valid {
  font-size: 10px;
  color: #9ca3af;
}

/* ================================================================
   空状态 / 加载
   ================================================================ */
.empty, .loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #9ca3af;
}

.empty i, .loading i {
  font-size: 36px;
  margin-bottom: 12px;
  opacity: .5;
}

/* ================================================================
   政策详情弹窗
   ================================================================ */
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.modal-box {
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  background: #fff;
  border-radius: 20px 20px 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideUp .3s ease;
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 12px;
  border-bottom: 1px solid #f3f4f6;
}

.modal-header h3 {
  font-size: 17px;
  font-weight: 700;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-close {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.modal-close:active { background: #e5e7eb; }
.modal-close i { font-size: 12px; color: #6b7280; }

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.modal-section {
  margin-bottom: 20px;
}

.modal-section:last-child { margin-bottom: 0; }

.modal-section h4 {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
}

.modal-section p {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
}

.modal-highlight {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  padding: 12px 16px;
}

.modal-highlight span {
  font-size: 13px;
  color: #9a3412;
}

.modal-highlight strong {
  font-size: 24px;
  font-weight: 900;
  color: #f97316;
}

.flow-steps {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.flow-step {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fs-num {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background: #f97316;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.flow-step span:last-child {
  font-size: 13px;
  color: #6b7280;
}

.modal-footer {
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #f3f4f6;
}

.modal-btn {
  width: 100%;
  padding: 12px 0;
  border: none;
  border-radius: 22px;
  background: linear-gradient(135deg, #f97316, #ea580c);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.modal-btn:active { opacity: .85; }

/* ================================================================
   响应式
   ================================================================ */
@media (max-width: 360px) {
  .banner { padding: 16px; }
  .banner-text h2 { font-size: 18px; }
  .mc-name { font-size: 14px; }
  .pi-img { height: 80px; }
}

@media (min-width: 481px) {
  .section { margin: 0 16px 20px; }
  .banner { margin: 16px; padding: 28px; }
  .banner-text h2 { font-size: 26px; }
  .modal-box { border-radius: 20px; margin-bottom: 20px; }
}
</style>
